<template>
  <section class="guest-detail" style="margin-top: 16px">
    <div class="guests-page__head" style="margin: 0 0 12px">
      <div>
        <h3 class="detail-block-title" style="margin: 0">Forniture</h3>
        <p class="guests-page__subtitle">
          Magazzino forniture personali (panni/presidi) e bolle di consegna.
        </p>
      </div>
      <div
        v-if="canManageSupplies"
        style="display: flex; gap: 8px; flex-wrap: wrap"
      >
        <button class="villa-btn villa-btn--ghost" @click="openItem()">
          <i class="fa-solid fa-plus"></i> Nuova fornitura
        </button>
        <button class="villa-btn villa-btn--primary" @click="openDelivery()">
          <i class="fa-solid fa-file-arrow-up"></i> Carica bolla
        </button>
      </div>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento…</div>
    <template v-else>
      <!-- Sottosezioni della tab Presidi igienico-sanitari -->
      <div class="supply-subtabs">
        <button
          v-for="s in subTabs"
          :key="s.key"
          class="supply-subtab"
          :class="{ 'supply-subtab--on': subTab === s.key }"
          @click="subTab = s.key"
        >
          {{ s.label }}
        </button>
      </div>

      <!-- Giacenza attuale -->
      <div v-show="subTab === 'giacenza'">
        <div v-if="!items.length" class="guests-loading">
          Nessuna fornitura. Crea la prima o carica una bolla.
        </div>
        <div v-else class="guests-table-wrap">
          <table class="guests-table">
            <thead>
              <tr>
                <th>Fornitura</th>
                <th>Categoria</th>
                <th>Stock</th>
                <th>Soglia</th>
                <th class="actions-cell">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="it in items" :key="it.id">
                <td>{{ it.name }}</td>
                <td>{{ catLabel(it.category) }}</td>
                <td>
                  <span :class="{ 'supply-low': it.lowStock }">
                    {{ it.currentStock }} {{ it.unit }}
                  </span>
                  <span
                    v-if="it.lowStock"
                    class="status-badge status-badge--suspended"
                    style="margin-left: 6px"
                  >
                    sotto soglia
                  </span>
                </td>
                <td>{{ it.minThreshold != null ? it.minThreshold : "—" }}</td>
                <td class="actions-cell">
                  <template v-if="canManageSupplies">
                    <button
                      class="icon-btn"
                      title="Carico/scarico"
                      @click="openMovement(it)"
                    >
                      <i class="fa-solid fa-right-left"></i>
                    </button>
                    <button
                      class="icon-btn"
                      title="Modifica"
                      @click="openEdit(it)"
                    >
                      <i class="fa-solid fa-pen"></i>
                    </button>
                    <button
                      class="icon-btn icon-btn--danger"
                      title="Cancella"
                      @click="openDelete(it)"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </template>
                  <span v-else class="supply-readonly-dash">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Carichi / Scarichi / Review: stessa tabella su lista filtrata -->
      <div v-show="['carichi', 'scarichi', 'review'].includes(subTab)">
        <div v-if="!movementsForTab.length" class="guests-loading">
          {{
            subTab === "review"
              ? "Nessuna rettifica o annullamento."
              : "Nessun movimento."
          }}
        </div>
        <div v-else class="guests-table-wrap">
          <table class="guests-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Fornitura</th>
                <th>Tipo</th>
                <th>Q.tà</th>
                <th>Prima → Dopo</th>
                <th>Causale</th>
                <th class="actions-cell"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="m in movementsForTab"
                :key="m.id"
                :class="{ 'supply-mv--voided': m.status === 'voided' }"
              >
                <td>{{ fmtDateTime(m.createdAt) }}</td>
                <td>{{ m.supplyItem ? m.supplyItem.name : "—" }}</td>
                <td>{{ mvLabel(m.movementType) }}</td>
                <td>{{ m.quantity }}</td>
                <td>{{ m.stockBefore }} → {{ m.stockAfter }}</td>
                <td>
                  {{ reasonLabel(m.reason) }}
                  <span
                    v-if="m.status === 'voided'"
                    class="status-badge status-badge--deceased"
                    :title="m.voidReason || ''"
                  >
                    Annullato
                  </span>
                </td>
                <td class="actions-cell">
                  <button
                    v-if="isAdminUser && m.status !== 'voided'"
                    class="icon-btn icon-btn--danger"
                    title="Annulla movimento"
                    @click="openVoid(m)"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Review stock: anomalie cambio panno (stock insufficiente, ecc.) -->
        <template v-if="subTab === 'review'">
          <h4
            class="detail-block-title"
            style="font-size: 14px; margin-top: 18px"
          >
            Anomalie da verificare
          </h4>
          <div
            v-if="!careAnomalies.length"
            class="guests-loading"
            style="padding: 12px"
          >
            Nessuna anomalia registrata.
          </div>
          <ul v-else class="supply-notes">
            <li v-for="a in careAnomalies" :key="a.id">
              <strong>{{ fmtDateTime(a.performedAt) }}</strong>
              <span v-if="a.operatorName"> · {{ a.operatorName }}</span>
              <span
                class="status-badge status-badge--suspended"
                style="margin-left: 6px"
              >
                da verificare
              </span>
              <ul class="supply-note-lines">
                <li
                  v-for="(w, i) in (a.metadata && a.metadata.supplyWarnings) ||
                  []"
                  :key="i"
                >
                  {{ w.item === "bed_pad" ? "Traversa" : "Panno" }}:
                  {{ anomalyLabel(w.reason) }}
                </li>
              </ul>
            </li>
          </ul>
        </template>
      </div>

      <!-- Documenti forniture collegati -->
      <div v-show="subTab === 'documenti'">
        <div v-if="!notes.length" class="guests-loading" style="padding: 16px">
          Nessuna bolla registrata.
        </div>
        <ul v-else class="supply-notes">
          <li v-for="n in notes" :key="n.id">
            <div class="supply-note-head">
              <div class="supply-note-meta">
                <strong>{{ n.ddtNumber || "Bolla" }}</strong>
                <span v-if="n.ddtDate"> · {{ fmtDate(n.ddtDate) }}</span>
                <span v-if="n.supplierName"> · {{ n.supplierName }}</span>
                <small> — {{ (n.lines || []).length }} righe</small>
                <span
                  v-if="!n.hasFile"
                  class="status-badge status-badge--suspended"
                  style="margin-left: 6px"
                >
                  {{
                    n.guestDocumentId ? "PDF archiviato" : "PDF non disponibile"
                  }}
                </span>
              </div>
              <div class="supply-note-actions">
                <button
                  class="icon-btn"
                  title="Visualizza PDF"
                  :disabled="!n.hasFile || docBusy.id === n.id"
                  @click="viewNote(n)"
                >
                  <i
                    :class="
                      docBusy.id === n.id && docBusy.action === 'view'
                        ? 'fa-solid fa-spinner fa-spin'
                        : 'fa-solid fa-eye'
                    "
                  ></i>
                </button>
                <button
                  class="icon-btn"
                  title="Stampa PDF"
                  :disabled="!n.hasFile || docBusy.id === n.id"
                  @click="printNote(n)"
                >
                  <i
                    :class="
                      docBusy.id === n.id && docBusy.action === 'print'
                        ? 'fa-solid fa-spinner fa-spin'
                        : 'fa-solid fa-print'
                    "
                  ></i>
                </button>
                <button
                  class="icon-btn"
                  title="Scarica PDF"
                  :disabled="!n.hasFile || docBusy.id === n.id"
                  @click="downloadNote(n)"
                >
                  <i
                    :class="
                      docBusy.id === n.id && docBusy.action === 'download'
                        ? 'fa-solid fa-spinner fa-spin'
                        : 'fa-solid fa-download'
                    "
                  ></i>
                </button>
                <button
                  v-if="canArchiveDdtPdf"
                  class="icon-btn icon-btn--danger"
                  title="Archivia PDF"
                  :disabled="!n.hasFile || docBusy.id === n.id"
                  @click="askArchiveNote(n)"
                >
                  <i class="fa-solid fa-box-archive"></i>
                </button>
              </div>
            </div>
            <ul class="supply-note-lines">
              <li v-for="l in n.lines" :key="l.id">
                {{ l.description }} — {{ l.quantity }} {{ l.unit }}
                <span v-if="l.lotNumber">· lotto {{ l.lotNumber }}</span>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </template>

    <!-- Modale fornitura -->
    <div
      v-if="itemModal.open"
      class="villa-modal-backdrop"
      @click.self="closeItem"
    >
      <div class="villa-confirm" style="max-width: 480px">
        <h3 class="villa-confirm__title">Nuova fornitura</h3>
        <div class="form-grid" style="margin-bottom: 16px">
          <div class="form-field form-field--full">
            <label>Nome <span class="required">*</span></label>
            <input
              v-model.trim="itemForm.name"
              type="text"
              placeholder="es. Pannolone taglia M"
            />
          </div>
          <div class="form-field">
            <label>Categoria</label>
            <select v-model="itemForm.category" class="search-box">
              <option value="absorbent_pad">Panno</option>
              <option value="bed_pad">Traversa</option>
              <option value="other">Altro</option>
            </select>
          </div>
          <div class="form-field">
            <label>Soglia minima</label>
            <input
              v-model.number="itemForm.minThreshold"
              type="number"
              min="0"
            />
          </div>
          <div class="form-field">
            <label>Giacenza iniziale</label>
            <input
              v-model.number="itemForm.initialStock"
              type="number"
              min="0"
            />
          </div>
        </div>
        <div class="villa-confirm__actions">
          <button class="villa-btn villa-btn--ghost" @click="closeItem">
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--primary"
            :disabled="itemModal.busy"
            @click="submitItem"
          >
            Crea
          </button>
        </div>
      </div>
    </div>

    <!-- Modale modifica fornitura (solo anagrafica: nome/categoria/soglia).
         Lo stock NON è modificabile qui: si gestisce solo via carico/scarico. -->
    <div
      v-if="editModal.open"
      class="villa-modal-backdrop"
      @click.self="closeEdit"
    >
      <div class="villa-confirm" style="max-width: 480px">
        <h3 class="villa-confirm__title">Modifica fornitura</h3>
        <div class="form-grid" style="margin-bottom: 16px">
          <div class="form-field form-field--full">
            <label>Nome <span class="required">*</span></label>
            <input v-model.trim="editForm.name" type="text" />
          </div>
          <div class="form-field">
            <label>Categoria</label>
            <select v-model="editForm.category" class="search-box">
              <option value="absorbent_pad">Panno</option>
              <option value="bed_pad">Traversa</option>
              <option value="other">Altro</option>
            </select>
          </div>
          <div class="form-field">
            <label>Soglia minima</label>
            <input
              v-model.number="editForm.minThreshold"
              type="number"
              min="0"
            />
          </div>
        </div>
        <div class="villa-confirm__actions">
          <button class="villa-btn villa-btn--ghost" @click="closeEdit">
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--primary"
            :disabled="editModal.busy"
            @click="submitEdit"
          >
            Salva
          </button>
        </div>
      </div>
    </div>

    <!-- Modale conferma cancellazione fornitura (hard delete lato backend, solo
         se giacenza 0 e nessun movimento/bolla collegati). -->
    <div
      v-if="deleteModal.open"
      class="villa-modal-backdrop"
      @click.self="deleteModal.open = false"
    >
      <div class="villa-confirm" style="max-width: 440px">
        <h3 class="villa-confirm__title">Elimina fornitura</h3>
        <p class="villa-confirm__text">
          Sei sicuro di voler eliminare questa fornitura? L'operazione non può
          essere annullata.
        </p>
        <div class="villa-confirm__actions">
          <button
            class="villa-btn villa-btn--ghost"
            :disabled="deleteModal.busy"
            @click="deleteModal.open = false"
          >
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--danger"
            :disabled="deleteModal.busy"
            @click="confirmDelete"
          >
            {{ deleteModal.busy ? "Eliminazione…" : "Elimina" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modale movimento manuale -->
    <div
      v-if="mvModal.open"
      class="villa-modal-backdrop"
      @click.self="closeMovement"
    >
      <div class="villa-confirm" style="max-width: 460px">
        <h3 class="villa-confirm__title">Movimento — {{ mvModal.itemName }}</h3>
        <div class="form-grid" style="margin-bottom: 16px">
          <div class="form-field">
            <label>Tipo</label>
            <select v-model="mvForm.movementType" class="search-box">
              <option value="load">Carico</option>
              <option value="unload">Scarico</option>
              <option value="adjustment">Rettifica (nuovo stock)</option>
            </select>
          </div>
          <div class="form-field">
            <label>{{
              mvForm.movementType === "adjustment" ? "Nuovo stock" : "Quantità"
            }}</label>
            <input v-model.number="mvForm.quantity" type="number" min="0" />
          </div>
          <div class="form-field form-field--full">
            <label>Note</label>
            <input v-model.trim="mvForm.notes" type="text" />
          </div>
        </div>
        <div class="villa-confirm__actions">
          <button class="villa-btn villa-btn--ghost" @click="closeMovement">
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--primary"
            :disabled="mvModal.busy"
            @click="submitMovement"
          >
            Registra
          </button>
        </div>
      </div>
    </div>

    <!-- Modale annullamento movimento (soft delete, motivo obbligatorio) -->
    <div
      v-if="voidModal.open"
      class="villa-modal-backdrop"
      @click.self="voidModal.open = false"
    >
      <div class="villa-confirm" style="max-width: 440px">
        <h3 class="villa-confirm__title">Annulla movimento</h3>
        <p class="villa-confirm__text">
          Sei sicuro di voler annullare questo movimento? L'operazione è
          tracciata (audit) e la giacenza verrà ricalcolata.
        </p>
        <div class="form-field form-field--full" style="margin-bottom: 16px">
          <label>Motivo <span class="required">*</span></label>
          <textarea v-model.trim="voidModal.reason" rows="2"></textarea>
        </div>
        <div class="villa-confirm__actions">
          <button
            class="villa-btn villa-btn--ghost"
            :disabled="voidModal.busy"
            @click="voidModal.open = false"
          >
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--danger"
            :disabled="voidModal.busy"
            @click="confirmVoid"
          >
            {{ voidModal.busy ? "Annullamento…" : "Conferma annullamento" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modale conferma archiviazione PDF bolla (soft delete del GuestDocument).
         NON elimina la bolla, le righe né i movimenti: solo il file PDF. -->
    <div
      v-if="archiveModal.open"
      class="villa-modal-backdrop"
      @click.self="archiveModal.open = false"
    >
      <div class="villa-confirm" style="max-width: 440px">
        <h3 class="villa-confirm__title">Archivia PDF bolla</h3>
        <p class="villa-confirm__text">
          Vuoi archiviare il PDF della bolla
          <strong>{{ archiveModal.label }}</strong
          >? La bolla, le righe e i movimenti di stock restano invariati: viene
          archiviato solo il file PDF, che non sarà più visualizzabile o
          scaricabile.
        </p>
        <div class="villa-confirm__actions">
          <button
            class="villa-btn villa-btn--ghost"
            :disabled="archiveModal.busy"
            @click="archiveModal.open = false"
          >
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--danger"
            :disabled="archiveModal.busy"
            @click="confirmArchiveNote"
          >
            {{ archiveModal.busy ? "Archiviazione…" : "Archivia PDF" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modale bolla multi-riga -->
    <div
      v-if="dnModal.open"
      class="villa-modal-backdrop"
      @click.self="closeDelivery"
    >
      <div
        class="villa-confirm"
        style="max-width: 680px; max-height: 90vh; overflow-y: auto"
      >
        <h3 class="villa-confirm__title">Carica bolla di consegna</h3>
        <div class="form-grid" style="margin-bottom: 12px">
          <div class="form-field">
            <label>N° DDT</label>
            <input v-model.trim="dnForm.ddtNumber" type="text" />
          </div>
          <div class="form-field">
            <label>Data DDT</label>
            <input v-model="dnForm.ddtDate" type="date" />
          </div>
          <div class="form-field">
            <label>Fornitore</label>
            <input
              v-model.trim="dnForm.supplierName"
              type="text"
              placeholder="es. SVAS BIOSANA"
            />
          </div>
          <div class="form-field">
            <label>File bolla (PDF/immagine)</label>
            <input ref="dnFile" type="file" accept=".pdf,image/*" />
          </div>
        </div>

        <h4 class="detail-block-title" style="font-size: 14px">
          Righe prodotto
        </h4>
        <!-- Autocomplete descrizione: riusa i nomi forniture già presenti per
             evitare doppioni/errori di battitura, senza bloccare nuovi prodotti. -->
        <datalist id="supply-item-names">
          <option v-for="n in itemNameOptions" :key="n" :value="n" />
        </datalist>
        <div v-for="(row, i) in dnForm.lines" :key="i" class="supply-row">
          <select
            v-model="row.category"
            class="search-box"
            style="min-width: 110px"
          >
            <option value="absorbent_pad">Panno</option>
            <option value="bed_pad">Traversa</option>
            <option value="other">Altro</option>
          </select>
          <input
            v-model.trim="row.description"
            placeholder="Descrizione"
            class="search-box"
            list="supply-item-names"
          />
          <input
            v-model.number="row.quantity"
            type="number"
            min="1"
            placeholder="Q.tà"
            class="search-box"
            style="max-width: 90px"
          />
          <input
            v-model.trim="row.lotNumber"
            placeholder="Lotto"
            class="search-box"
            style="max-width: 110px"
          />
          <input
            v-model="row.expiryDate"
            type="date"
            class="search-box"
            style="max-width: 150px"
          />
          <button
            class="icon-btn icon-btn--danger"
            :disabled="dnForm.lines.length === 1"
            @click="dnForm.lines.splice(i, 1)"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
        <button
          class="villa-btn villa-btn--ghost"
          style="margin-top: 8px"
          @click="addLine"
        >
          <i class="fa-solid fa-plus"></i> Aggiungi riga
        </button>

        <div class="villa-confirm__actions" style="margin-top: 16px">
          <button class="villa-btn villa-btn--ghost" @click="closeDelivery">
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--primary"
            :disabled="dnModal.busy"
            @click="submitDelivery"
          >
            <i v-if="dnModal.busy" class="fa-solid fa-spinner fa-spin"></i>
            Carica bolla e aggiorna stock
          </button>
        </div>
      </div>
    </div>

    <!-- Conferma custom uscita con modifiche non salvate (condivisa da tutte le
         modali del pannello tramite il mixin discardGuard). -->
    <DiscardChangesModal
      :open="discardGuard.open"
      @stay="onDiscardStay"
      @discard="onDiscardConfirm"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  printPdfFromResponse,
  openPdfFromResponse,
  isPopupBlocked,
  POPUP_BLOCKED_MESSAGE,
} from "@/utils/printPdf";
import DiscardChangesModal from "@/components/DiscardChangesModal.vue";
import discardGuard from "@/mixins/discardGuard";

const CAT = { absorbent_pad: "Panno", bed_pad: "Traversa", other: "Altro" };
const MV = { load: "Carico", unload: "Scarico", adjustment: "Rettifica" };
const REASON = {
  delivery_note: "Bolla",
  diaper_change: "Cambio panno",
  manual_adjustment: "Rettifica manuale",
  correction: "Correzione",
  other: "Altro",
};

export default {
  name: "GuestSuppliesPanel",
  components: { DiscardChangesModal },
  mixins: [discardGuard],
  props: { guestId: { type: [String, Number], required: true } },
  emits: ["changed"],
  data() {
    return {
      loading: true,
      subTab: "giacenza",
      subTabs: [
        { key: "giacenza", label: "Giacenza attuale" },
        { key: "carichi", label: "Carichi / forniture" },
        { key: "scarichi", label: "Scarichi / utilizzi" },
        { key: "review", label: "Review stock" },
        { key: "documenti", label: "Documenti forniture" },
      ],
      items: [],
      notes: [],
      movements: [],
      careAnomalies: [],
      itemModal: { open: false, busy: false },
      itemForm: {
        name: "",
        category: "absorbent_pad",
        minThreshold: null,
        initialStock: 0,
      },
      editModal: { open: false, busy: false, itemId: null },
      editForm: { name: "", category: "absorbent_pad", minThreshold: null },
      editBaseline: "",
      deleteModal: { open: false, busy: false, itemId: null, itemName: "" },
      mvModal: { open: false, busy: false, itemId: null, itemName: "" },
      mvForm: { movementType: "load", quantity: 1, notes: "" },
      voidModal: { open: false, busy: false, movementId: null, reason: "" },
      dnModal: { open: false, busy: false },
      dnForm: { ddtNumber: "", ddtDate: "", supplierName: "", lines: [] },
      // Snapshot dei form all'apertura, per il dirty-check in chiusura modale.
      itemBaseline: "",
      mvBaseline: "",
      dnBaseline: "",
      // Stato azioni PDF bolla, per-riga (id bolla + azione in corso).
      docBusy: { id: null, action: null },
      archiveModal: { open: false, busy: false, noteId: null, label: "" },
    };
  },
  computed: {
    ...mapGetters(["isAdminUser", "canManageSupplies", "canArchiveDdtPdf"]),
    // Nomi forniture già censite per l'ospite (autocomplete righe bolla).
    itemNameOptions() {
      return [...new Set(this.items.map((i) => i.name).filter(Boolean))];
    },
    movementsForTab() {
      if (this.subTab === "carichi") {
        return this.movements.filter((m) => m.movementType === "load");
      }
      if (this.subTab === "scarichi") {
        return this.movements.filter((m) => m.movementType === "unload");
      }
      if (this.subTab === "review") {
        return this.movements.filter(
          (m) => m.status === "voided" || m.movementType === "adjustment"
        );
      }
      return [];
    },
  },
  methods: {
    ...mapActions([
      "fetchGuestSupplies",
      "createGuestSupply",
      "updateGuestSupply",
      "deleteGuestSupply",
      "createSupplyMovement",
      "fetchSupplyMovements",
      "createSupplyDeliveryNote",
      "fetchSupplyDeliveryNotes",
      "voidSupplyMovement",
      "fetchGuestCareActivities",
      "downloadGuestDocument",
      "archiveGuestDocument",
    ]),
    // ---- Azioni PDF bolla (riusa GuestDocument collegato; nessuna rigenerazione) ----
    noteLabel(n) {
      return n.ddtNumber || (n.ddtDate ? this.fmtDate(n.ddtDate) : "bolla");
    },
    async viewNote(n) {
      if (!n.hasFile || !n.guestDocumentId) return;
      this.docBusy = { id: n.id, action: "view" };
      try {
        const res = await this.downloadGuestDocument({
          guestId: this.guestId,
          documentId: n.guestDocumentId,
        });
        openPdfFromResponse(res);
      } catch (err) {
        this.$toast.error(
          isPopupBlocked(err)
            ? POPUP_BLOCKED_MESSAGE
            : err?.response?.data?.message ||
                "PDF non disponibile o archiviato."
        );
      } finally {
        this.docBusy = { id: null, action: null };
      }
    },
    async printNote(n) {
      if (!n.hasFile || !n.guestDocumentId) return;
      this.docBusy = { id: n.id, action: "print" };
      try {
        const res = await this.downloadGuestDocument({
          guestId: this.guestId,
          documentId: n.guestDocumentId,
        });
        printPdfFromResponse(res);
      } catch (err) {
        this.$toast.error(
          isPopupBlocked(err)
            ? POPUP_BLOCKED_MESSAGE
            : err?.response?.data?.message || "Stampa non riuscita."
        );
      } finally {
        this.docBusy = { id: null, action: null };
      }
    },
    async downloadNote(n) {
      if (!n.hasFile || !n.guestDocumentId) return;
      this.docBusy = { id: n.id, action: "download" };
      let url = null;
      try {
        const res = await this.downloadGuestDocument({
          guestId: this.guestId,
          documentId: n.guestDocumentId,
        });
        const blob = new Blob([res.data], {
          type: res.data?.type || "application/pdf",
        });
        url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = n.documentName || `bolla-${n.ddtNumber || n.id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Download non riuscito."
        );
      } finally {
        if (url) window.URL.revokeObjectURL(url);
        this.docBusy = { id: null, action: null };
      }
    },
    askArchiveNote(n) {
      if (!n.hasFile) return;
      this.archiveModal = {
        open: true,
        busy: false,
        noteId: n.id,
        documentId: n.guestDocumentId,
        label: this.noteLabel(n),
      };
    },
    async confirmArchiveNote() {
      this.archiveModal.busy = true;
      try {
        await this.archiveGuestDocument({
          guestId: this.guestId,
          documentId: this.archiveModal.documentId,
        });
        this.$toast.success("PDF della bolla archiviato.");
        this.archiveModal.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Archiviazione non riuscita."
        );
      } finally {
        this.archiveModal.busy = false;
      }
    },
    anomalyLabel(r) {
      return (
        {
          stock_insufficiente: "stock insufficiente",
          fornitura_non_trovata: "fornitura non trovata",
          categoria_errata: "categoria errata",
        }[r] || r
      );
    },
    openVoid(m) {
      this.voidModal = {
        open: true,
        busy: false,
        movementId: m.id,
        reason: "",
      };
    },
    async confirmVoid() {
      if (!this.voidModal.reason) {
        return this.$toast.error("Inserisci il motivo dell'annullamento.");
      }
      this.voidModal.busy = true;
      try {
        await this.voidSupplyMovement({
          guestId: this.guestId,
          movementId: this.voidModal.movementId,
          reason: this.voidModal.reason,
        });
        this.$toast.success("Movimento annullato.");
        this.voidModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Annullamento non riuscito."
        );
      } finally {
        this.voidModal.busy = false;
      }
    },
    // ---- Dirty-check chiusura modali (evita perdita dati non salvati) ----
    // Tutti i punti di chiusura (backdrop + Annulla) passano da requestClose:
    // se il form è modificato mostra la conferma custom, altrimenti chiude subito.
    closeItem() {
      this.requestClose(
        JSON.stringify(this.itemForm) !== this.itemBaseline,
        () => {
          this.itemModal.open = false;
        }
      );
    },
    closeMovement() {
      this.requestClose(JSON.stringify(this.mvForm) !== this.mvBaseline, () => {
        this.mvModal.open = false;
      });
    },
    closeDelivery() {
      this.requestClose(JSON.stringify(this.dnForm) !== this.dnBaseline, () => {
        this.dnModal.open = false;
      });
    },
    catLabel: (c) => CAT[c] || c,
    mvLabel: (m) => MV[m] || m,
    reasonLabel: (r) => REASON[r] || r,
    fmtDate(d) {
      if (!d) return "—";
      const s = String(d).slice(0, 10).split("-");
      return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : d;
    },
    fmtDateTime(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return "—";
      const p = (n) => String(n).padStart(2, "0");
      return `${p(dt.getDate())}/${p(dt.getMonth() + 1)} ${p(
        dt.getHours()
      )}:${p(dt.getMinutes())}`;
    },
    async load() {
      this.loading = true;
      try {
        const [items, notes, movements, care] = await Promise.all([
          this.fetchGuestSupplies(this.guestId),
          this.fetchSupplyDeliveryNotes(this.guestId),
          this.fetchSupplyMovements({ guestId: this.guestId }),
          this.fetchGuestCareActivities({
            guestId: this.guestId,
            type: "diaper_change",
          }).catch(() => []),
        ]);
        this.items = items;
        this.notes = notes;
        this.movements = movements.slice(0, 50);
        // Anomalie: cambi panno con avvisi di stock (review stock).
        this.careAnomalies = (care || []).filter(
          (a) => a.metadata && a.metadata.supplyWarnings
        );
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento forniture non riuscito."
        );
      } finally {
        this.loading = false;
      }
    },
    openItem() {
      this.itemForm = {
        name: "",
        category: "absorbent_pad",
        minThreshold: null,
        initialStock: 0,
      };
      this.itemModal = { open: true, busy: false };
      this.itemBaseline = JSON.stringify(this.itemForm);
    },
    async submitItem() {
      if (!this.itemForm.name) return this.$toast.error("Inserisci il nome.");
      this.itemModal.busy = true;
      try {
        await this.createGuestSupply({
          guestId: this.guestId,
          payload: { ...this.itemForm },
        });
        this.$toast.success("Fornitura creata.");
        this.itemModal.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Creazione non riuscita."
        );
      } finally {
        this.itemModal.busy = false;
      }
    },
    openEdit(it) {
      this.editForm = {
        name: it.name || "",
        category: it.category || "absorbent_pad",
        minThreshold: it.minThreshold != null ? it.minThreshold : null,
      };
      this.editModal = { open: true, busy: false, itemId: it.id };
      this.editBaseline = JSON.stringify(this.editForm);
    },
    closeEdit() {
      this.requestClose(
        JSON.stringify(this.editForm) !== this.editBaseline,
        () => {
          this.editModal.open = false;
        }
      );
    },
    async submitEdit() {
      if (!this.editForm.name) return this.$toast.error("Inserisci il nome.");
      this.editModal.busy = true;
      try {
        await this.updateGuestSupply({
          guestId: this.guestId,
          supplyItemId: this.editModal.itemId,
          payload: {
            name: this.editForm.name,
            category: this.editForm.category,
            minThreshold: this.editForm.minThreshold,
          },
        });
        this.$toast.success("Fornitura aggiornata.");
        this.editModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Aggiornamento non riuscito."
        );
      } finally {
        this.editModal.busy = false;
      }
    },
    openDelete(it) {
      this.deleteModal = {
        open: true,
        busy: false,
        itemId: it.id,
        itemName: it.name,
      };
    },
    async confirmDelete() {
      this.deleteModal.busy = true;
      try {
        await this.deleteGuestSupply({
          guestId: this.guestId,
          supplyItemId: this.deleteModal.itemId,
        });
        this.$toast.success("Fornitura eliminata.");
        this.deleteModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Eliminazione non riuscita."
        );
      } finally {
        this.deleteModal.busy = false;
      }
    },
    openMovement(it) {
      this.mvModal = {
        open: true,
        busy: false,
        itemId: it.id,
        itemName: it.name,
      };
      this.mvForm = { movementType: "load", quantity: 1, notes: "" };
      this.mvBaseline = JSON.stringify(this.mvForm);
    },
    async submitMovement() {
      this.mvModal.busy = true;
      try {
        await this.createSupplyMovement({
          guestId: this.guestId,
          supplyItemId: this.mvModal.itemId,
          payload: { ...this.mvForm, reason: "manual_adjustment" },
        });
        this.$toast.success("Movimento registrato.");
        this.mvModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Movimento non riuscito."
        );
      } finally {
        this.mvModal.busy = false;
      }
    },
    openDelivery() {
      this.dnForm = {
        ddtNumber: "",
        ddtDate: "",
        supplierName: "",
        lines: [this.newLine()],
      };
      this.dnModal = { open: true, busy: false };
      this.dnBaseline = JSON.stringify(this.dnForm);
    },
    newLine() {
      return {
        description: "",
        category: "absorbent_pad",
        quantity: 1,
        lotNumber: "",
        expiryDate: "",
      };
    },
    addLine() {
      this.dnForm.lines.push(this.newLine());
    },
    async submitDelivery() {
      const valid = this.dnForm.lines.filter(
        (l) => l.description && Number(l.quantity) > 0
      );
      if (!valid.length) {
        return this.$toast.error(
          "Inserisci almeno una riga con descrizione e quantità."
        );
      }
      const file = this.$refs.dnFile?.files?.[0];
      const fd = new FormData();
      if (file) fd.append("file", file, file.name);
      fd.append("ddtNumber", this.dnForm.ddtNumber || "");
      if (this.dnForm.ddtDate) fd.append("ddtDate", this.dnForm.ddtDate);
      fd.append("supplierName", this.dnForm.supplierName || "");
      fd.append("lines", JSON.stringify(valid));
      this.dnModal.busy = true;
      try {
        await this.createSupplyDeliveryNote({
          guestId: this.guestId,
          formData: fd,
        });
        this.$toast.success("Bolla caricata e stock aggiornato.");
        this.dnModal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento bolla non riuscito."
        );
      } finally {
        this.dnModal.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style scoped>
.villa-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 43, 87, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}
.supply-subtabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--villa-azure, #dceaf7);
  padding-bottom: 8px;
}
.supply-subtab {
  border: 1px solid var(--villa-azure, #dceaf7);
  background: #fff;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: var(--villa-navy, #082b57);
  cursor: pointer;
}
.supply-subtab--on {
  background: var(--villa-navy, #082b57);
  color: #fff;
  border-color: var(--villa-navy, #082b57);
}
.supply-low {
  color: #b91c1c;
  font-weight: 700;
}
.supply-mv--voided td {
  opacity: 0.55;
  text-decoration: line-through;
}
.supply-mv--voided td:last-child {
  text-decoration: none;
}
.supply-notes,
.supply-note-lines {
  list-style: none;
  padding: 0;
  margin: 0;
}
.supply-notes > li {
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.supply-note-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap;
}
.supply-note-meta {
  min-width: 0;
}
.supply-note-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.supply-note-lines {
  margin-top: 6px;
  padding-left: 14px;
  font-size: 13px;
  color: #475569;
}
.supply-note-lines li {
  list-style: disc;
}
.supply-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
</style>
